/**
 * Listing-level agent policy (BR-ENABLE-CASCADE / BR-AGENT-POLICY-SCOPE) — the single client source
 * of truth: one react-query key read by the header pill, the setup state and AgentPolicyEditor, and
 * invalidated on save. Types derive from the boundary contract in `@resida/api-types/agent`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentEnablementDecision,
  AgentPolicyResponse,
  AgentReviewDecision,
  PolicySource,
} from '@resida/api-types/agent';

function listingAgentPolicyQueryKey(listingId: string) {
  return ['listing-agent-policy', listingId] as const;
}

async function readPolicyResponse(response: Response): Promise<AgentPolicyResponse> {
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const err = (await response.json()) as { message?: string };
      if (err.message) message = err.message;
    } catch {
      /* non-JSON error body: keep the status text */
    }
    throw new Error(message);
  }
  return response.json() as Promise<AgentPolicyResponse>;
}

async function getListingAgentPolicy(listingId: string): Promise<AgentPolicyResponse> {
  const response = await fetch(
    `/landlord/api/listings/${encodeURIComponent(listingId)}/agent-policy`,
    { method: 'GET', credentials: 'include' }
  );
  return readPolicyResponse(response);
}

async function patchListingAgentPolicy(
  listingId: string,
  body: { enablement: AgentEnablementDecision; review: AgentReviewDecision }
): Promise<AgentPolicyResponse> {
  const response = await fetch(
    `/landlord/api/listings/${encodeURIComponent(listingId)}/agent-policy`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  return readPolicyResponse(response);
}

/** The listing's stored + resolved agent policy. GET requires listing#manage, so callers pass `enabled = false` for viewers. */
export function useListingAgentPolicy(listingId: string, enabled = true) {
  return useQuery({
    queryKey: listingAgentPolicyQueryKey(listingId),
    queryFn: () => getListingAgentPolicy(listingId),
    enabled: enabled && listingId !== '',
  });
}

/**
 * Saves BOTH values explicitly at listing scope (never 'inherit' — the UI has no inherit control),
 * then invalidates the shared key so the pill, setup row and editor all update in place.
 */
export function useConfirmListingAgentPolicy(listingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: { enablement: 'enabled' | 'disabled'; review: 'required' | 'not_required' }) =>
      patchListingAgentPolicy(listingId, { enablement: values.enablement, review: values.review }),
    onSuccess: (data) => {
      queryClient.setQueryData(listingAgentPolicyQueryKey(listingId), data);
      void queryClient.invalidateQueries({ queryKey: listingAgentPolicyQueryKey(listingId) });
    },
  });
}

/**
 * Where an effective value comes from, in the landlord's words. A value nobody set (the platform's own fallback)
 * has no source to name, so it returns null and the caller shows the value alone.
 */
export function describePolicySource(source: PolicySource): string | null {
  if (source === 'listing') return 'set for this listing';
  if (source === 'landlord') return 'your account default';
  if (source === 'lead') return 'set for this lead';
  if (source === 'case') return 'set for this case';
  return null;
}
